import { useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { ErrorBanner, formatUtc, useAsync } from '../../components/Async'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { PageTitle } from '../../components/PageTitle'
import {
  ApiError,
  catalogs,
  policies,
  trust,
  type GroupMemberPublication,
  type PolicyGroupDetail,
  type PublicationStatus
} from '../../lib/api'
import { GroupMessagePicker } from './GroupMessagePicker'
import { groupProgress, memberLabel } from './policyGroups'
import { publishedRevision } from './publishOutcome'

/** Typed before retiring a group is allowed to stop its policies running, as on the list page. */
const STOP_PHRASE = 'stop the fleet'

/**
 * One builder choice and the policies it became.
 *
 * The agent takes one assistant on one host per policy, so "Claude, in a browser and the desktop app,
 * with a screenshot" is four policies. This page is where they are one thing again: approved together,
 * published together, retired together. Each member still has its own page, behind Advanced
 * settings, for the somebody who needs a selector or an interval the builder does not ask about; a
 * member edited there is marked, because "publish all" quietly shipping an edit nobody remembered
 * making is the surprise this page must not spring.
 */
export function PolicyGroupPage() {
  const { groupId } = useParams<{ groupId: string }>()
  const handedOver = useLocation().state as { created?: boolean; warnings?: string[] } | null
  const group = useAsync(() => policies.group(groupId!), [groupId])
  const builder = useAsync(() => catalogs.builder(), [])

  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(handedOver?.created ? 'Created as drafts. Nothing has reached a device yet.' : null)
  const [outcomes, setOutcomes] = useState<GroupMemberPublication[] | null>(null)

  const [confirmingPublish, setConfirmingPublish] = useState(false)
  const [running, setRunning] = useState<Record<string, PublicationStatus | null> | null>(null)
  const [confirmingRetire, setConfirmingRetire] = useState(false)
  const [reason, setReason] = useState('')
  const [alsoRevoke, setAlsoRevoke] = useState(true)

  const run = async (work: () => Promise<string>) => {
    setBusy(true)
    setFailure(null)
    setNotice(null)
    try {
      setNotice(await work())
      group.reload()
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'The request failed.')
      group.reload()
    } finally {
      setBusy(false)
    }
  }

  if (!group.value) {
    return (
      <>
        <div className="page-header">
          <div>
            <PageTitle>A policy group</PageTitle>
          </div>
        </div>
        <ErrorBanner message={group.error} />
        {!group.error && <div className="empty">Loading…</div>}
      </>
    )
  }

  const detail = group.value
  const progress = groupProgress(detail)
  const active = detail.members.filter((member) => member.status === 'active')

  const approveAll = () =>
    run(async () => {
      const result = await policies.approveGroup(detail.groupId)
      return result.alreadyApproved > 0
        ? `Approved ${result.approved}; ${result.alreadyApproved} already were. Publish when you are ready.`
        : `Approved all ${result.approved}. Publish when you are ready.`
    })

  /**
   * Opens the confirmation and asks, per member, what is running now. Each answer is the server's
   * count of devices reporting that publication; nothing here adds them up, because one device runs
   * several members and a sum would count it several times.
   */
  const askToPublish = async () => {
    setConfirmingPublish(true)
    setRunning(null)
    const answers: Record<string, PublicationStatus | null> = {}
    await Promise.all(
      active.map(async (member) => {
        try {
          answers[member.policyId] = await trust.publication(member.policyId)
        } catch {
          answers[member.policyId] = null
        }
      })
    )
    setRunning(answers)
  }

  const publishAll = () =>
    run(async () => {
      const result = await policies.publishGroup(detail.groupId)
      setOutcomes(result.members)
      if (result.failed > 0)
        throw new ApiError(400, `Published ${result.published} of ${result.members.length}. The rest are listed below with the reason.`)
      return `Published all ${result.published}. Each device picks them up at its next check-in.`
    })

  const retireAll = () =>
    run(async () => {
      let stopped = 0
      if (alsoRevoke)
        for (const member of active) {
          try {
            await trust.revoke(member.policyId, reason)
            stopped++
          } catch (error) {
            // Never published, or already revoked. Not a reason to keep the group.
            if (!(error instanceof ApiError) || error.status !== 400) throw error
          }
        }
      const result = await policies.retireGroup(detail.groupId, reason)
      setConfirmingRetire(false)
      setReason('')
      return (
        `Retired ${result.retired.length} ${result.retired.length === 1 ? 'policy' : 'policies'}` +
        (alsoRevoke ? `, and stopped ${stopped} running on the fleet.` : '. Any that were published are still running.')
      )
    })

  return (
    <>
      <div className="page-header">
        <div>
          <PageTitle>{detail.name}</PageTitle>
          <p>
            {detail.recipeLabel ?? detail.recipeKey}
            {detail.assistantDisplayName ? ` · ${detail.assistantDisplayName}` : ' · The whole computer'}
            {' · '}
            <span className={detail.status === 'active' ? 'pill' : 'pill'}>{detail.status}</span>
          </p>
        </div>
        <div className="toolbar">
          <button
            type="button"
            onClick={() => void approveAll()}
            disabled={busy || detail.status !== 'active' || !progress.awaitingApproval}
            data-testid="group-approve"
          >
            Approve all
          </button>
          <button
            type="button"
            className="primary"
            onClick={() => void askToPublish()}
            disabled={busy || !progress.readyToPublish}
            data-testid="group-publish"
          >
            Publish all
          </button>
          <button
            type="button"
            className="danger"
            onClick={() => setConfirmingRetire(true)}
            disabled={busy || detail.status !== 'active'}
            data-testid="group-retire"
          >
            Retire group
          </button>
        </div>
      </div>

      {detail.recipeExplanation && <p className="lede">{detail.recipeExplanation}</p>}

      <ErrorBanner message={failure} />
      {notice && (
        <div className="banner success" role="status" data-testid="group-notice">
          {notice}
        </div>
      )}
      {(handedOver?.warnings ?? []).map((warning) => (
        <div key={warning} className="banner warning" role="note" data-testid="group-warning">
          {warning}
        </div>
      ))}
      {detail.target === 'assistant' && builder.value && (
        <p className="hint" role="note" data-testid="group-prompt-observation-note">
          {builder.value.promptObservationNote}
        </p>
      )}

      <div className="card">
        <div className="card-body">
          <p data-testid="group-progress">{progress.summary}</p>
          {progress.customized > 0 && (
            <div className="banner warning" role="note" data-testid="group-customized">
              {progress.customized === 1 ? 'One policy here was' : `${progress.customized} policies here were`} edited by
              hand under Advanced settings. Approve all and Publish all include those edits.
            </div>
          )}
        </div>
        <div className="table-scroll">
          <table data-testid="group-members">
            <thead>
              <tr>
                <th>Covers</th>
                <th>Policy</th>
                <th>State</th>
                <th className="secondary">Created</th>
                <th aria-label="Settings" />
              </tr>
            </thead>
            <tbody>
              {detail.members.map((member) => (
                <tr key={member.policyId} data-testid={`group-member-${member.policyId}`}>
                  <td>
                    {memberLabel(member)}
                    {member.customized && (
                      <span className="pill" data-testid="member-customized">
                        {' '}
                        edited by hand
                      </span>
                    )}
                  </td>
                  <td className="muted">{member.name}</td>
                  <td>
                    <MemberState member={member} />
                  </td>
                  <td className="muted secondary">{formatUtc(detail.createdUtc)}</td>
                  <td>
                    <Link to={`/policies/${member.policyId}?advanced=1`} data-testid="member-advanced">
                      Advanced settings
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <GroupMessagePicker group={detail} onChanged={group.reload} />

      {outcomes && (
        <div className="card" style={{ marginTop: '1rem' }} data-testid="group-publish-outcomes">
          <div className="card-body">
            <h3 style={{ marginTop: 0 }}>What was published</h3>
            <ul className="choice-list">
              {outcomes.map((outcome) => (
                <li key={outcome.policyId}>
                  <strong>{outcome.name}</strong>:{' '}
                  {outcome.published
                    ? `${publishedRevision(outcome.revision, outcome.revisedFrom)}${outcome.replacedExisting ? ', replacing the one before' : ''}.`
                    : `not published. ${outcome.problem ?? ''}`}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmingPublish}
        title={`Publish ${active.length} ${active.length === 1 ? 'policy' : 'policies'} to the fleet`}
        confirmLabel="Publish to every device"
        busy={busy}
        onConfirm={() => {
          setConfirmingPublish(false)
          void publishAll()
        }}
        onClose={() => setConfirmingPublish(false)}
        data-testid="group-publish-confirm"
      >
        <p>
          Each policy in this group is assigned to every enrolled device in this organization, and each
          device starts running it at its next check-in. Every one is compiled first; if any cannot be,
          nothing is published.
        </p>
        {running === null ? (
          <p className="hint">Checking what is running now…</p>
        ) : (
          <ul className="choice-list" data-testid="group-publish-replacing">
            {active.map((member) => {
              const now = running[member.policyId]
              return (
                <li key={member.policyId}>
                  {memberLabel(member)}:{' '}
                  {now && !now.revokedUtc
                    ? `revision ${now.revision} is published, and ${now.devicesReportingActive} ${now.devicesReportingActive === 1 ? 'device reports' : 'devices report'} running it.`
                    : 'nothing published yet.'}
                </li>
              )
            })}
          </ul>
        )}
        {progress.customized > 0 && (
          <p className="hint">Policies edited by hand are published as edited.</p>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        open={confirmingRetire}
        title={`Retire ${detail.name}`}
        confirmLabel={alsoRevoke ? 'Retire and stop them running' : 'Retire, leave them running'}
        danger={alsoRevoke}
        busy={busy}
        confirmDisabled={!reason.trim()}
        confirmPhrase={alsoRevoke ? STOP_PHRASE : undefined}
        onConfirm={() => void retireAll()}
        onClose={() => setConfirmingRetire(false)}
        data-testid="group-retire-confirm"
      >
        <p>
          Every policy in the group stops being editable or publishable. Their revisions and the events
          they produced are kept.
        </p>
        <div className="field">
          <label htmlFor="group-retire-reason">Why</label>
          <input
            id="group-retire-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="No longer needed."
            data-testid="group-retire-reason"
          />
        </div>
        <label className="inline-check">
          <input
            type="checkbox"
            checked={alsoRevoke}
            onChange={(event) => setAlsoRevoke(event.target.checked)}
            data-testid="group-retire-also-revoke"
          />
          <span>Also stop devices running them</span>
        </label>
        <div className="hint">
          Retiring alone changes nothing on any machine. Revoking serves a signed tombstone, and every
          device holding one of these policies stops running it at its next check-in.
        </div>
      </ConfirmDialog>
    </>
  )
}

function MemberState({ member }: { member: PolicyGroupDetail['members'][number] }) {
  if (member.status === 'retired') return <span className="muted">Retired</span>
  if (member.latestStatus === 'approved') return <span className="pill sev-2">Approved, revision {member.latestRevision}</span>
  if (member.approvedRevision)
    return (
      <span>
        Draft revision {member.latestRevision} <span className="muted">· revision {member.approvedRevision} approved</span>
      </span>
    )
  return <span className="muted">Draft, not approved</span>
}
